import React, { useState } from 'react';

import { useGetEvent } from '@vlabs/api/luna-api/client';
import type { EventId } from '@vlabs/api/luna-api/model/EventId';
import { PlusIcon } from '@vlabs/icons';
import { ButtonSwitch, ButtonSwitchGroup } from '@vlabs/uikit';
import type { WithDataTestid, WithClassNameProp } from '@vlabs/uikit/types';
import cn from 'classnames';

import { FindSimilarDropdown } from '@vlabs/monokit/features/point/find-similar';
import { buildTestid } from '@vlabs/monokit/shared/lib/utils';
import { AttachFaceToCardButton } from '@vlabs/monokit/widgets/attach-face-to-card-button/@x/event-details';

import st from './EventDetection.module.sass';
import { useEventDetectionTranslation } from './i18n';
import { EventDetectionAttributes } from './tabs/EventDetectionAttributes';
import { EventDetectionProperties } from './tabs/EventDetectionProperties';

type StatsType = 'attributes' | 'properties';
interface EventDetectionProps extends WithDataTestid, WithClassNameProp {
  eventId: EventId;
  canAttachFaceToCard?: boolean;
  canSearch?: boolean;
}

const displayName = 'EventDetection';
const testid = buildTestid(displayName);

function EventDetection({
  eventId,
  className = undefined,
  'data-testid': dataTestId = undefined,
  canAttachFaceToCard = true,
  canSearch = true,
}: EventDetectionProps): React.JSX.Element {
  const { t } = useEventDetectionTranslation();

  const [statsType, setStatsType] = useState<StatsType>('attributes');

  const { data: event } = useGetEvent(eventId, { target: ['face_id'] });
  const faceId = event?.face_id;

  return (
    <div
      className={cn(st.Root, className)}
      {...testid(dataTestId)}
    >
      <div className={st.Heading}>
        <ButtonSwitchGroup
          onChange={setStatsType}
          size="small"
          value={statsType}
          variant="square"
          {...testid(dataTestId)}
        >
          <ButtonSwitch value="attributes">
            {t('attributes')}
          </ButtonSwitch>

          <ButtonSwitch value="properties">
            {t('additional_properties')}
          </ButtonSwitch>
        </ButtonSwitchGroup>

        <div className={st.Actions}>
          {!!canAttachFaceToCard && !!faceId && (
            <AttachFaceToCardButton
              faceId={faceId}
              icon={<PlusIcon />}
              size="small"
              {...testid(dataTestId)}
            >
              {t('button_face_to_card')}
            </AttachFaceToCardButton>
          )}
          {!!canSearch && (
            <FindSimilarDropdown
              eventId={eventId}
              type="detection"
              {...testid(dataTestId)}
            />
          )}
        </div>
      </div>
      <div className={st.Content}>
        {statsType === 'attributes' && <EventDetectionAttributes eventId={eventId} />}
        {statsType === 'properties' && <EventDetectionProperties eventId={eventId} />}
      </div>
    </div>
  );
}

EventDetection.displayName = displayName;

export { EventDetection };
